import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, formatTarih, formatTl, waLink as waNo } from '../lib/api';
import { useAuth } from '../lib/auth';
import Ipucu from '../ui/Ipucu';
import FaturasizCikis from './FaturasizCikis';
import SiparisVadeleri, { useSiparisVadeleri } from '../ui/SiparisVadeleri';
import KasaHesapSec, { kasaAnahtari, useKasaHesaplari, type KasaHesabi } from '../ui/KasaHesapSec';
import AltSayfa from '../ui/AltSayfa';
import TutarGiris from '../ui/TutarGiris';
import { useTelefon } from '../ui/mobil';

type Satir = {
  id: number; customer: { id: number; name: string; phone: string | null };
  amountKurus: number; paidKurus: number; kalanKurus: number;
  status: string; dueNote: string | null; createdAt: string; gun: number;
  kirmizi: boolean; reminderStage: number; lastReminderAt: string | null;
  satirlar: { name?: string; qty?: number }[]; note: string | null;
};
type Veri = {
  satirlar: Satir[];
  ozet: {
    acikAdet: number; acikToplamKurus: number;
    gecikmisAdet: number; gecikmisToplamKurus: number; enEskiGun: number;
  };
  hatirlatmaGunleri: number[];
};

export default function Tahsilat() {
  const { can } = useAuth();
  /** SAHA çıkış girer ama havuzu göremez (roller.ts). Havuz isteği atılmaz. */
  const havuzGorur = can('receivable.view');
  const [durum, setDurum] = useState<'acik' | 'kapandi' | 'hepsi'>('acik');
  const [veri, setVeri] = useState<Veri | null>(null);
  const [hata, setHata] = useState<string | null>(null);
  const [bilgi, setBilgi] = useState<string | null>(null);
  const [cikisAcik, setCikisAcik] = useState(false);
  const [odemeId, setOdemeId] = useState<number | null>(null);
  const [odemeTutar, setOdemeTutar] = useState('');
  /** İsteğe bağlı: tahsilatın girdiği kasa / banka hesabı (yalnız kasaya yazabilen rolde). */
  const kasa = useKasaHesaplari();
  const [odemeHesap, setOdemeHesap] = useState('');
  const [odemeAnahtar, setOdemeAnahtar] = useState('');
  /* Telefon düzeni: cari ara → alacak kartları → tutar (büyük sayısal klavye) → kasa (düğmeler) → onay. */
  const telefon = useTelefon();
  const [cariAra, setCariAra] = useState('');
  const [isleniyor, setIsleniyor] = useState(false);

  const yukle = useCallback(() => {
    if (!havuzGorur) return;
    api<Veri>(`/api/tahsilat?durum=${durum}`).then(setVeri).catch((e) => setHata((e as Error).message));
  }, [durum, havuzGorur]);
  useEffect(yukle, [yukle]);
  /** Üstteki kartlar listeyi süzer: hepsi / yalnız 30 günü geçenler / en eskiden yeniye. */
  const [kartSuzgec, setKartSuzgec] = useState<'hepsi' | 'gecikmis' | 'eski'>('hepsi');
  /** Siparişlerin ödeme planından vadesi gelenler (faturasız alacaklardan AYRI liste). */
  const { veri: vadeler, yukle: vadeYukle } = useSiparisVadeleri(havuzGorur);

  async function odemeIsle(id: number) {
    setHata(null);
    if (isleniyor) return;
    setIsleniyor(true);
    try {
      const r = await api<{ kalanKurus: number; kapandi: boolean; kasaHareketId: number | null }>(`/api/tahsilat/${id}/odeme`, {
        method: 'POST',
        body: JSON.stringify({ tutarTl: Number(odemeTutar), ...(odemeHesap ? { hesapId: Number(odemeHesap), islemAnahtari: odemeAnahtar } : {}) }),
      });
      setBilgi((r.kapandi ? 'Alacak kapandı.' : `Kısmi tahsilat işlendi. Kalan ${formatTl(r.kalanKurus)}`) + (r.kasaHareketId ? ' Kasa girişi yazıldı.' : ''));
      setOdemeId(null); setOdemeTutar(''); setOdemeHesap('');
      yukle();
    } catch (e) {
      setHata(e instanceof Error ? e.message : 'Tahsilat işlenemedi');
    } finally { setIsleniyor(false); }
  }

  async function hatirlat(s: Satir) {
    try {
      await api(`/api/tahsilat/${s.id}/hatirlat`, { method: 'POST' });
      setBilgi(`${s.customer.name} için hatırlatma kaydedildi.`);
      yukle();
    } catch (e) {
      setHata(e instanceof Error ? e.message : 'Hatırlatma yazılamadı');
    }
  }

  /** WhatsApp derin linki — WhatsApp API'si gerekmez, tek dokunuş. */
  const waLink = (s: Satir) =>
    waNo(s.customer.phone,
      `Merhaba ${s.customer.name},\n${formatTl(s.kalanKurus)} tutarındaki ödemenizi hatırlatmak istedik.\n\nArt Mozaik & Seramik`);

  /**
   * SAHA görünümü. Denetimde çıktı: Burak'ın menüsünde bu ekran yoktu, adresi
   * yazınca da "yetkiniz yok" görüyordu — işin en kritik akışı (cepten
   * faturasız çıkış) arayüzde kapalıydı; yalnız API açıktı.
   */
  if (!havuzGorur) {
    return (
      <>
        <div>
          <h1 className="ccd-title">Faturasız Çıkış</h1>
          <div className="ccd-meta">Çıkan malı girin: stok faturasız haneden düşer, alacak kendiliğinden açılır.</div>
        </div>
        {bilgi && (
          <div className="ccd-banner">
            <span className="ccd-banner-icon" aria-hidden="true">✓</span><span>{bilgi}</span>
          </div>
        )}
        {can('informal.write') ? (
          <button className="ccd-btn ccd-btn-primary ccd-btn-lg" onClick={() => { setBilgi(null); setCikisAcik(true); }}
                  data-tur="faturasiz-cikis">
            + Faturasız Çıkış Gir
          </button>
        ) : (
          <div className="ccd-banner ccd-banner-danger">
            <span className="ccd-banner-icon" aria-hidden="true">!</span><span>Bu ekran rolünüzde yok.</span>
          </div>
        )}
        <div className="ccd-card ccd-meta">
          Tahsilat havuzu ve tutar toplamları rolünüzde görünmez. Girdiğiniz her çıkış
          yöneticinin Tahsilat ekranına alacak olarak düşer.
        </div>
        {cikisAcik && (
          <FaturasizCikis kapat={() => setCikisAcik(false)}
                          eklendi={() => { setCikisAcik(false); setBilgi('Çıkış kaydedildi. Stok düştü, alacak açıldı.'); }} />
        )}
      </>
    );
  }

  if (hata && !veri) {
    return (
      <div className="ccd-banner ccd-banner-danger">
        <span className="ccd-banner-icon" aria-hidden="true">!</span><span>{hata}</span>
      </div>
    );
  }

  return (
    <>
      <div className="ccd-row-between" style={{ flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 className="ccd-title">Tahsilat</h1>
          <div className="ccd-meta">
            Faturasız her çıkış otomatik alacak açar · 30 günü geçen kırmızı
          </div>
        </div>
        <div className="ccd-row" style={{ gap: 8, flexWrap: 'wrap' }}>
          {can('reminder.view') && (
            <Link className="ccd-btn ccd-btn-sm" to="/tahsilat/hatirlatma" data-tur="borc-hatirlatma"
                  title="Carilere SMS ve e-posta ile borç hatırlatması: borçlular, onay kuyruğu, ayarlar">
              ✉ Borç hatırlatma
            </Link>
          )}
          {can('informal.write') && (
            <button className="ccd-btn ccd-btn-primary ccd-btn-sm" onClick={() => setCikisAcik(true)} data-tur="faturasiz-cikis">
              + Faturasız Çıkış
            </button>
          )}
        </div>
      </div>

      {veri && (
        <div className={`ccd-grid ${vadeler ? 'ccd-grid-4' : 'ccd-grid-3'}`} data-tur="ozet">
          <button type="button" className="ccd-stat ccd-stat-tik" aria-pressed={kartSuzgec === 'hepsi' && durum === 'acik'}
                  onClick={() => { setDurum('acik'); setKartSuzgec('hepsi'); }} title="Açık alacakların hepsini listele">
            <div className="ccd-stat-label">Açık alacak <Ipucu id="faturasiz-alacak" /></div>
            <div className="ccd-stat-value">{formatTl(veri.ozet.acikToplamKurus)}</div>
            <div className="ccd-stat-foot">{veri.ozet.acikAdet} kayıt · listele →</div>
          </button>
          <button type="button" className="ccd-stat ccd-stat-tik" aria-pressed={kartSuzgec === 'gecikmis'}
                  onClick={() => { setDurum('acik'); setKartSuzgec('gecikmis'); }} title="Yalnız 30 günü geçenleri listele">
            <div className="ccd-stat-label">30 günü geçen <Ipucu id="otuz-gun-gecen" /></div>
            <div className="ccd-stat-value" style={{ color: veri.ozet.gecikmisAdet ? 'var(--ccd-danger)' : undefined }}>
              {formatTl(veri.ozet.gecikmisToplamKurus)}
            </div>
            <div className="ccd-stat-foot">{veri.ozet.gecikmisAdet} kayıt · listele →</div>
          </button>
          <button type="button" className="ccd-stat ccd-stat-tik" aria-pressed={kartSuzgec === 'eski'}
                  onClick={() => { setDurum('acik'); setKartSuzgec('eski'); }} title="En eskiden yeniye sırala">
            <div className="ccd-stat-label">En eski <Ipucu id="alacak-hatirlatma" /></div>
            <div className="ccd-stat-value">{veri.ozet.enEskiGun} <span style={{ fontSize: 14 }}>gün</span></div>
            <div className="ccd-stat-foot">
              Hatırlatma günleri: {veri.hatirlatmaGunleri.join(' · ')} · en eskiden sırala →
            </div>
          </button>
          {vadeler && (
            <button type="button" className="ccd-stat ccd-stat-tik" title="Sipariş vadeleri bölümüne in"
                    onClick={() => document.getElementById('siparis-vadeleri')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}>
              <div className="ccd-stat-label">Vadesi geçen sipariş ödemesi <Ipucu id="siparis-vade-odemesi" /></div>
              <div className="ccd-stat-value" style={{ color: vadeler.ozet.gecmis.adet ? 'var(--ccd-danger)' : undefined }}>
                {formatTl(vadeler.ozet.gecmis.tutarKurus)}
              </div>
              <div className="ccd-stat-foot">
                {vadeler.ozet.gecmis.adet} ödeme{vadeler.ozet.bugun.adet > 0 ? ` · bugün ${vadeler.ozet.bugun.adet}` : ''} · {vadeler.ozet.yaklasan.adet} yaklaşan →
              </div>
            </button>
          )}
        </div>
      )}

      {bilgi && (
        <div className="ccd-banner">
          <span className="ccd-banner-icon" aria-hidden="true">✓</span><span>{bilgi}</span>
        </div>
      )}
      {hata && (
        <div className="ccd-banner ccd-banner-danger">
          <span className="ccd-banner-icon" aria-hidden="true">!</span><span>{hata}</span>
        </div>
      )}

      <div className="ccd-tabs" role="tablist">
        {([['acik', 'Açık'], ['kapandi', 'Kapanan'], ['hepsi', 'Tümü']] as const).map(([id, ad]) => (
          <button key={id} role="tab" aria-selected={durum === id} className="ccd-tab"
                  onClick={() => setDurum(id)}>{ad}</button>
        ))}
      </div>

      {!veri ? (
        <div className="ccd-inline-note">Yükleniyor…</div>
      ) : veri.satirlar.length === 0 ? (
        <div className="ccd-empty">
          {durum === 'acik' ? 'Açık alacak yok — havuz temiz.' : 'Kayıt yok.'}
        </div>
      ) : telefon ? (
        <>
          <label className="ccd-sa-ara ccd-th-ara">
            <span aria-hidden="true">⌕</span>
            <input type="search" inputMode="search" enterKeyHint="search" autoComplete="off" autoCorrect="off" spellCheck={false}
                   value={cariAra} onChange={(e) => setCariAra(e.target.value)} placeholder="Cari adı ya da telefon" aria-label="Alacaklarda cari ara" />
            {cariAra && <button type="button" className="ccd-sa-sil" aria-label="Aramayı temizle" onClick={() => setCariAra('')}>✕</button>}
          </label>
          {(() => {
            const duz = (m: string) => m.toLocaleLowerCase('tr').replace(/ı/g, 'i');
            const q = duz(cariAra.trim());
            const liste = (kartSuzgec === 'gecikmis' ? veri.satirlar.filter((s) => s.kirmizi)
              : kartSuzgec === 'eski' ? [...veri.satirlar].sort((a, b) => b.gun - a.gun) : veri.satirlar)
              .filter((s) => !q || duz(`${s.customer.name} ${s.customer.phone ?? ''}`).includes(q));
            if (liste.length === 0) return <div className="ccd-empty">“{cariAra.trim()}” ile eşleşen alacak yok.</div>;
            return (
              <ul className="ccd-th-liste">
                {liste.map((s) => (
                  <li key={s.id} className={`ccd-th-kart${s.kirmizi && s.status !== 'KAPANDI' ? ' ccd-th-kirmizi' : ''}`}>
                    <div className="ccd-th-ust">
                      <div className="ccd-th-cari">
                        <Link to={`/crm/${s.customer.id}`}><b>{s.customer.name}</b></Link>
                        <span className="ccd-meta">
                          {s.satirlar.map((l) => `${l.name ?? ''}${l.qty ? ` ×${l.qty}` : ''}`).join(', ') || '—'}
                        </span>
                        <span className="ccd-meta">{formatTarih(s.createdAt)}{s.dueNote ? ` · vade: ${s.dueNote}` : ''}{s.note ? ` · ${s.note}` : ''}</span>
                      </div>
                      <div className="ccd-th-tutar">
                        {s.status === 'KAPANDI' ? <span className="ccd-badge ccd-badge-success">kapandı</span> : <b>{formatTl(s.kalanKurus)}</b>}
                        {s.status !== 'KAPANDI' && <span className={s.kirmizi ? 'ccd-kpi-down' : 'ccd-meta'}>{s.gun} gün</span>}
                        {s.status !== 'KAPANDI' && s.kalanKurus !== s.amountKurus && <span className="ccd-meta">toplam {formatTl(s.amountKurus)}</span>}
                      </div>
                    </div>
                    {s.status === 'ACIK' && can('receivable.write') && (
                      <div className="ccd-th-eylem">
                        <button type="button" className="ccd-btn ccd-btn-primary"
                                onClick={() => { setOdemeId(s.id); setOdemeTutar(String(s.kalanKurus / 100)); setOdemeHesap(''); setOdemeAnahtar(kasaAnahtari()); }}>
                          Tahsil et
                        </button>
                        <button type="button" className="ccd-btn"
                                title={s.lastReminderAt ? `Son: ${formatTarih(s.lastReminderAt)} (${s.reminderStage}. kademe)` : 'Henüz hatırlatılmadı'}
                                onClick={() => hatirlat(s)}>
                          Hatırlat{s.reminderStage > 0 ? ` (${s.reminderStage})` : ''}
                        </button>
                        {waLink(s) && <a className="ccd-btn" href={waLink(s)!} target="_blank" rel="noreferrer">WhatsApp</a>}
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            );
          })()}
          {odemeId != null && (() => {
            const s = veri.satirlar.find((x) => x.id === odemeId);
            if (!s) return null;
            const kurus = odemeTutar === '' ? null : Math.round(Number(odemeTutar) * 100);
            const gecerli = kurus != null && Number.isFinite(kurus) && kurus > 0 && kurus <= s.kalanKurus;
            return (
              <AltSayfa baslik="Tahsilat gir" alt={<>{s.customer.name} · kalan <b>{formatTl(s.kalanKurus)}</b></>} kapat={() => setOdemeId(null)} tam
                        eylem={<>
                          <button type="button" className="ccd-btn" onClick={() => setOdemeId(null)} disabled={isleniyor}>Vazgeç</button>
                          <button type="button" className="ccd-btn ccd-btn-primary" disabled={!gecerli || isleniyor} onClick={() => void odemeIsle(s.id)}>
                            {isleniyor ? 'İşleniyor…' : gecerli ? `${formatTl(kurus!)} tahsil et` : 'Tutar yazın'}
                          </button>
                        </>}>
                {hata && <div className="ccd-banner ccd-banner-danger" role="alert"><span className="ccd-banner-icon" aria-hidden="true">!</span><span>{hata}</span></div>}
                <div className="ccd-field ccd-th-buyuk">
                  <label className="ccd-field-label" htmlFor="th-tutar">Tahsil edilen tutar</label>
                  <TutarGiris id="th-tutar" kurus={kurus} degis={(k) => setOdemeTutar(k == null ? '' : String(k / 100))} autoFocus />
                  <div className="ccd-th-hizli">
                    <button type="button" className="ccd-btn" onClick={() => setOdemeTutar(String(s.kalanKurus / 100))}>Tamamı</button>
                    <button type="button" className="ccd-btn" onClick={() => setOdemeTutar(String(Math.round(s.kalanKurus / 2) / 100))}>Yarısı</button>
                  </div>
                  {kurus != null && kurus > s.kalanKurus && <div className="ccd-field-error">Tutar kalandan ({formatTl(s.kalanKurus)}) büyük olamaz.</div>}
                  {gecerli && kurus! < s.kalanKurus && <div className="ccd-field-hint">Kısmi tahsilat: {formatTl(s.kalanKurus - kurus!)} açık kalır.</div>}
                </div>
                {kasa.acik && (
                  <div className="ccd-field">
                    <span className="ccd-field-label">Hangi kasaya girdi? <span className="ccd-muted">(isteğe bağlı)</span></span>
                    {kasa.hesaplar == null ? <div className="ccd-meta">Hesaplar yükleniyor…</div>
                      : kasa.hesaplar.length === 0 ? <KasaHesapSec hesaplar={kasa.hesaplar} deger={odemeHesap} degis={setOdemeHesap} />
                      : (
                        <div className="ccd-th-kasalar" role="radiogroup" aria-label="Kasa / banka hesabı">
                          <button type="button" role="radio" aria-checked={odemeHesap === ''} className={`ccd-th-kasa${odemeHesap === '' ? ' ccd-is-active' : ''}`} onClick={() => setOdemeHesap('')}>
                            <b>Kasaya yazma</b><span>yalnız alacak kapanır</span>
                          </button>
                          {kasa.hesaplar.map((h: KasaHesabi) => (
                            <button type="button" key={h.id} role="radio" aria-checked={odemeHesap === String(h.id)}
                                    className={`ccd-th-kasa${odemeHesap === String(h.id) ? ' ccd-is-active' : ''}`} onClick={() => setOdemeHesap(String(h.id))}>
                              <b>{h.ad}</b><span>{h.tur === 'KASA' ? 'Kasa' : h.tur === 'BANKA' ? 'Banka' : 'Emanet'}{h.resmi ? '' : ' · gayriresmî'}</span>
                            </button>
                          ))}
                        </div>
                      )}
                  </div>
                )}
              </AltSayfa>
            );
          })()}
        </>
      ) : (
        <div className="ccd-table-wrap">
          <table className="ccd-table">
            <thead>
              <tr>
                <th className="ccd-th">Cari</th>
                <th className="ccd-th">Ne çıktı <Ipucu id="faturasiz-cikis" /></th>
                <th className="ccd-th ccd-td-num">Tutar</th>
                <th className="ccd-th ccd-td-num">Kalan <Ipucu id="faturasiz-alacak" /></th>
                <th className="ccd-th ccd-td-num">Gün <Ipucu id="alacak-hatirlatma" /></th>
                <th className="ccd-th">İşlem</th>
              </tr>
            </thead>
            <tbody>
              {(kartSuzgec === 'gecikmis' ? veri.satirlar.filter((s) => s.kirmizi)
                : kartSuzgec === 'eski' ? [...veri.satirlar].sort((a, b) => b.gun - a.gun) : veri.satirlar).map((s) => (
                <tr key={s.id} className="ccd-tr">
                  <td className="ccd-td">
                    <div><Link to={`/crm/${s.customer.id}`} title="Cari kartı ve ekstre"><b>{s.customer.name}</b></Link></div>
                    {s.customer.phone && <div className="ccd-meta">{s.customer.phone}</div>}
                    {s.dueNote && <div className="ccd-meta">vade: {s.dueNote}</div>}
                  </td>
                  <td className="ccd-td ccd-meta">
                    {s.satirlar.map((l) => `${l.name ?? ''}${l.qty ? ` ×${l.qty}` : ''}`).join(', ') || '—'}
                    {s.note && <div>{s.note}</div>}
                    <div>{formatTarih(s.createdAt)}</div>
                  </td>
                  <td className="ccd-td ccd-td-num">{formatTl(s.amountKurus)}</td>
                  <td className="ccd-td ccd-td-num">
                    {s.status === 'KAPANDI'
                      ? <span className="ccd-badge ccd-badge-success">kapandı</span>
                      : <b>{formatTl(s.kalanKurus)}</b>}
                  </td>
                  <td className="ccd-td ccd-td-num">
                    {s.status === 'KAPANDI' ? <span className="ccd-muted">—</span> : (
                      <span className={s.kirmizi ? 'ccd-kpi-down' : ''}>{s.gun}</span>
                    )}
                  </td>
                  <td className="ccd-td ccd-td-nowrap">
                    {s.status === 'ACIK' && can('receivable.write') && (
                      odemeId === s.id ? (
                        <span className="ccd-kasa-sec">
                          <span className="ccd-row" style={{ gap: 6 }}>
                            <input className="ccd-input" type="number" inputMode="decimal" step="any" autoFocus
                                   style={{ width: 110 }} placeholder="Tutar"
                                   value={odemeTutar} onChange={(e) => setOdemeTutar(e.target.value)} />
                            <button className="ccd-btn ccd-btn-primary ccd-btn-sm"
                                    onClick={() => odemeIsle(s.id)}>İşle</button>
                            <button className="ccd-btn ccd-btn-ghost ccd-btn-sm"
                                    onClick={() => setOdemeId(null)}>✕</button>
                          </span>
                          {/* Hesap seçilirse aynı işlemde kasa girişi yazılır; seçilmezse eski davranış. */}
                          {kasa.acik && <KasaHesapSec hesaplar={kasa.hesaplar} deger={odemeHesap} degis={setOdemeHesap} />}
                        </span>
                      ) : (
                        <span className="ccd-row" style={{ gap: 6 }}>
                          <button className="ccd-btn ccd-btn-sm"
                                  onClick={() => { setOdemeId(s.id); setOdemeTutar(String(s.kalanKurus / 100)); setOdemeHesap(''); setOdemeAnahtar(kasaAnahtari()); }}>
                            Tahsil et
                          </button>
                          <button className="ccd-btn ccd-btn-ghost ccd-btn-sm"
                                  title={s.lastReminderAt ? `Son: ${formatTarih(s.lastReminderAt)} (${s.reminderStage}. kademe)` : 'Henüz hatırlatılmadı'}
                                  onClick={() => hatirlat(s)}>
                            Hatırlat{s.reminderStage > 0 ? ` (${s.reminderStage})` : ''}
                          </button>
                          {waLink(s) && (
                            <a className="ccd-btn ccd-btn-ghost ccd-btn-sm" href={waLink(s)!}
                               target="_blank" rel="noreferrer">WhatsApp</a>
                          )}
                        </span>
                      )
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Sipariş vadeleri: ödeme planından vadesi geçen · bugün · yaklaşan 14 gün */}
      {vadeler && <SiparisVadeleri veri={vadeler} yenile={vadeYukle} />}

      {cikisAcik && (
        <FaturasizCikis kapat={() => setCikisAcik(false)} eklendi={() => { setCikisAcik(false); yukle(); }} />
      )}
    </>
  );
}
